import { TextField } from 'components/Field/TextField';
import { ICONS } from 'components/Icon';
import { IconButton } from 'components/IconButton';
import { Logo } from 'components/Logo';
import { Menu } from 'components/Menu';
import { NavigationHeader } from 'components/NavigationHeader';
import { SeparatorHorizontal } from 'components/SeparatorHorizontal';
import { useSelector } from 'hooks/useSelector';
import { filterArrayEmpty, openUrl } from 'lib/riskbase';
import { theme } from 'lib/theme';
import * as React from 'react';
import { Platform, View } from 'react-native';
import { useDispatch } from 'react-redux';
import { OPEN_CLOUD_ASSETS, SET_ASSETS_FILTER } from 'store/actions';
import {
  selectAssetListSearchText,
  selectAssetsTotal,
  selectChosenAssetsCount,
} from 'store/assets/selectors';
import { selectUserFeatureCreateAsset } from 'store/auth/selectors';
import { REMOTE_LOAD_CREATE_ASSET } from 'store/remote/actions';

import { ViewWithSeparators } from 'components/ViewWithSeparators';
import { HeaderRow } from 'components/HeaderRow';
import { Button, ButtonTheme } from 'components/Button';
import { Filters } from './Filters';

export const LocalAssetsHeader: React.FC = () => {
  const dispatch = useDispatch();
  const hasAssetsDownloaded = !!useSelector(selectAssetsTotal);
  const chosenAssetsCount = useSelector(selectChosenAssetsCount);
  const canCreateAsset = useSelector(selectUserFeatureCreateAsset);
  const assetsListSearchText = useSelector(selectAssetListSearchText);

  const currentSearchString = React.useRef<string>(assetsListSearchText);
  const [showSearch, setShowSearch] =
    React.useState<boolean>(!!assetsListSearchText);

  const openSearch = React.useCallback(
    () => dispatch(OPEN_CLOUD_ASSETS()),
    [dispatch],
  );
  const handleSearch = React.useCallback(
    (search: string) => {
      currentSearchString.current = search;
      dispatch(SET_ASSETS_FILTER({ search }));
    },
    [dispatch],
  );

  const handleShowSearch = () => {
    setShowSearch(true);
  };

  const handleSearchBlur = () => {
    if (!currentSearchString.current) {
      setShowSearch(false);
    }
  };

  const buttonDownloadAssets = {
    icon: ICONS.CloudLight,
    key: 'download-assets',
    onPress: () => setTimeout(openSearch, 1),
    text: 'Download Assets',
  };

  const buttonCreateNewAsset = {
    icon: ICONS.PlusLight,
    key: 'create-new-asset',
    onPress: () => dispatch(REMOTE_LOAD_CREATE_ASSET()),
    text: 'New Asset',
  };

  const showPlannedMaintenanceUntil = new Date('2025-04-23T08:00:00Z');
  const showPlannedMaintenance = new Date() < showPlannedMaintenanceUntil;

  return (
    <>
      <NavigationHeader marginLeft={0} marginRight={0}>
        {!showSearch ? (
          <View
            style={{
              flex: 1,
              marginLeft: theme.paddingHorizontalHalf,
              marginRight: theme.paddingHorizontal,
            }}
          >
            <Logo height={32} />
          </View>
        ) : (
          <TextField
            value={assetsListSearchText}
            iconLeft={ICONS.MagnifyingGlassLight}
            onChange={handleSearch}
            onBlur={handleSearchBlur}
            placeholder="Search Downloaded Assets"
            clearable
            autoFocus
            containerStyle={{
              backgroundColor: '#ffffff',
              borderRadius: 4,
              flex: 5,
              height: 40,
              overflow: 'hidden',
            }}
            disabled={chosenAssetsCount !== 0}
            autoCorrect={false}
            autoComplete="off"
            autoCapitalize="none"
          />
        )}
        <ViewWithSeparators
          horizontal
          style={{
            marginLeft: theme.paddingHorizontal,
          }}
          separatorMargin={theme.paddingHorizontalHalf}
          separatorColor="transparent"
        >
          {!showSearch && hasAssetsDownloaded && (
            <IconButton
              icon={ICONS.MagnifyingGlassLight}
              onPress={handleShowSearch}
              disabled={chosenAssetsCount !== 0}
            />
          )}
          {canCreateAsset ? (
            <Menu
              iconButtonConfig={{
                color: theme.colors.blue,
                icon: ICONS.CloudLight,
                iconSize: 24,
                disabled: chosenAssetsCount !== 0,
              }}
              options={[buttonDownloadAssets, buttonCreateNewAsset].filter(
                filterArrayEmpty,
              )}
            />
          ) : (
            <IconButton
              key={buttonDownloadAssets.key}
              onPress={buttonDownloadAssets.onPress}
              icon={buttonDownloadAssets.icon}
              iconSize={24}
            />
          )}
        </ViewWithSeparators>
      </NavigationHeader>
      {showPlannedMaintenance && (
        <>
          <SeparatorHorizontal />
          <View
            style={{
              backgroundColor: theme.colors.white,
            }}
          >
            <View
              style={{
                backgroundColor: theme.colors.orange10,
                paddingHorizontal: theme.paddingHorizontal,
                padding: theme.paddingHorizontalHalf,
              }}
            >
              <Button
                buttonTheme={ButtonTheme.link}
                text="Planned Performance Improvements"
                style={{ alignSelf: 'center' }}
                onPress={() =>
                  openUrl('https://riskbase.uk/infrastructure-upgrade')
                }
              />
            </View>
          </View>
        </>
      )}
      {hasAssetsDownloaded && (
        <>
          <SeparatorHorizontal />
          <HeaderRow horizontalScrollView={Platform.OS !== 'web'}>
            <Filters />
          </HeaderRow>
        </>
      )}
    </>
  );
};
